import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, BookOpen, Gavel, Quote } from "lucide-react";
import MotionSubscribeBox from "../components/MotionSubscribeBox";

const PRACTICE_LABELS = {
  "criminal-defense": "Federal Criminal Defense",
  "section-1983": "§1983 Civil Rights",
  "appellate": "Federal Appellate",
  "first-amendment": "First Amendment",
  "prosecutor-misconduct": "Prosecutor Misconduct",
  "official-misconduct": "Official Misconduct",
};

export default function MotionDetail() {
  const { slug } = useParams();
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/defenders/motions/${slug}`).then(r => setM(r.data)).catch(() => setM(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-20" data-testid="motion-loading">Loading…</div>;
  if (!m) return (
    <div className="max-w-7xl mx-auto px-6 py-20" data-testid="motion-not-found">
      <h1 className="display-black text-3xl uppercase text-[#064E3B]">Motion not found.</h1>
      <Link to="/defenders/motions" className="btn-ghost mt-6 inline-flex">All Motions</Link>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="motion-detail">
      <Link to="/defenders/motions" className="mono text-[11px] tracking-[0.22em] uppercase text-[#D97706] hover:underline inline-flex items-center gap-2">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to the archive
      </Link>

      <div className="mt-6">
        <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#D97706]">
          Filed {m.week_of?.slice(0, 10)} · {m.case_type}
        </div>
        <h1 className="display-black text-4xl lg:text-5xl uppercase text-[#064E3B] mt-4 leading-tight tracking-tighter max-w-5xl">{m.title}</h1>
        <div className="mt-5 flex flex-wrap gap-2">
          <span className="mono text-[10px] tracking-[0.22em] uppercase px-2 py-1 border border-[#D97706] text-[#D97706]">{PRACTICE_LABELS[m.practice_area] || m.practice_area}</span>
          <span className="mono text-[10px] tracking-[0.22em] uppercase px-2 py-1 border border-gray-300 text-gray-700">{m.court}</span>
          <span className="mono text-[10px] tracking-[0.22em] uppercase px-2 py-1 border border-gray-300 text-gray-700">Filed by {m.filed_by}</span>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <article className="lg:col-span-2">
          <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#D97706]">// The Motion</div>
          <h2 className="display-black text-xl uppercase text-[#064E3B] mt-2">Preamble</h2>
          <p className="text-base text-gray-800 mt-3 italic leading-relaxed">{m.preamble}</p>

          <h2 className="display-black text-xl uppercase text-[#064E3B] mt-8">Body</h2>
          <pre className="mt-3 text-sm text-gray-900 whitespace-pre-wrap font-sans border-l-4 border-[#064E3B] pl-5 leading-relaxed bg-gray-50 p-6 overflow-x-auto" data-testid="motion-detail-body">
{m.body}
          </pre>
        </article>

        <aside className="space-y-6">
          <div className="border border-[#D97706] bg-red-50 p-6" data-testid="motion-detail-teardown">
            <Quote className="w-5 h-5 text-[#D97706]" />
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#D97706] mt-2">// Founder Teardown</div>
            <pre className="mt-3 text-sm text-gray-900 whitespace-pre-wrap font-sans leading-relaxed">{m.founder_teardown}</pre>
            <div className="mono text-[10px] tracking-wider uppercase text-[#D97706] mt-5">— The Defenders Founder</div>
          </div>

          <div className="border border-gray-300 p-6 bg-gray-50">
            <BookOpen className="w-4 h-4 text-[#064E3B]" />
            <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#064E3B] mt-2">// Authorities Cited</div>
            <ul className="mt-3 space-y-2">
              {m.authorities.map(a => (
                <li key={a} className="mono text-[12px] text-[#064E3B] flex gap-2">
                  <span className="text-[#D97706]">▸</span> {a}
                </li>
              ))}
            </ul>
          </div>

          <div className="border border-[#064E3B] bg-[#064E3B] text-white p-6">
            <Gavel className="w-5 h-5 text-[#FACC15]" />
            <div className="display-black text-lg uppercase mt-3">Why It Matters</div>
            <p className="text-sm text-white/85 mt-2">{m.why_it_matters}</p>
          </div>

          <MotionSubscribeBox variant="light" />
        </aside>
      </div>
    </div>
  );
}
